import { useState } from "react";
import { RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { STORE_STATE, STORES, DSP_ID_TO_NAME } from "@/constants/catalog";
import { t, fmtDateTime } from "@/i18n";
import { cn } from "@/lib/utils";

const LEGACY = { "Instagram / Facebook": ["facebookmusic", "facebookfingerprinting"], "YouTube": ["youtubemusic", "youtube"], "Claro Música": ["imusica"], "KKBox": ["kkbox"], "JOOX": ["joox"] };

export const PlatformStatus = ({ rel, onUpdated }) => {
  const { can } = useAuth();
  const [busy, setBusy] = useState(false);
  const staff = can("admin", "manager");
  const cells = rel.store_status || [];
  const selectedIds = new Set((rel.stores || []).flatMap((s) => LEGACY[s] || [Object.keys(DSP_ID_TO_NAME).find((id) => DSP_ID_TO_NAME[id] === s)].filter(Boolean)));
  const rows = cells.filter((c) => selectedIds.size === 0 || selectedIds.has(c.dsp));
  const approvedCount = rows.filter((c) => c.state === "published").length;

  const sync = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/releases/${rel.id}/sync-distribution`); onUpdated(data); toast.success(data.status === "approved" ? t("¡Aprobado en todas las plataformas!") : t("Estado de plataformas actualizado")); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const approve = async () => {
    try { const { data } = await api.post(`/releases/${rel.id}/approve`); onUpdated(data); toast.success(t("Marcado como Aprobado")); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!["completed", "approved"].includes(rel.status) && cells.length === 0) return null;
  return (
    <section className="panel p-6 rise" data-testid="platform-status">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-lg font-semibold">{t("Estado por plataforma")}</h2>
          <p className="text-xs text-muted-foreground" data-testid="platform-status-summary">
            {cells.length ? `${t("{a}/{b} aprobadas", { a: approvedCount, b: rows.length })}${staff ? ` · ${t("actualizado {d}", { d: fmtDateTime(rel.distribution_synced_at) })}` : ""}` : t("Aún sin datos de las plataformas")}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="platform-status-schedule">
            {staff ? (rel.all_stores_approved ? t("Aprobado en las 56 plataformas · seguimiento finalizado") : t("Se revisa automáticamente cada 4 horas hasta que las 56 plataformas estén aprobadas"))
              : t("Cada plataforma revisa tu lanzamiento por separado; aquí verás cuáles ya lo aprobaron.")}
          </p>
        </div>
        {staff && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={sync} disabled={busy} className="gap-1.5" data-testid="btn-sync-distribution"><RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} /> {busy ? t("Consultando…") : t("Sincronizar estado")}</Button>
            {rel.status !== "approved" && <Button size="sm" onClick={approve} className="gap-1.5" data-testid="btn-approve-release"><CheckCircle2 className="h-3.5 w-3.5" /> {t("Marcar aprobado")}</Button>}
          </div>
        )}
      </div>
      {rows.length > 0 ? (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {rows.map((c) => {
            const st = STORE_STATE[c.state] || STORE_STATE.unknown;
            return (
              <li key={c.dsp} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm" data-testid={`store-status-${c.dsp}`} data-state={c.state}>
                <span className="truncate">{c.name}</span>
                <span className={cn("text-xs font-medium shrink-0 ml-2", st.cls)}>{t(st.label)}{staff && c.text && c.state !== "published" ? ` · ${c.text.split(" ").slice(-1)[0]}` : ""}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="flex flex-wrap gap-1.5">{(rel.stores.length ? rel.stores : STORES).map((s) => <span key={s} className="text-xs rounded-full border border-border px-2.5 py-0.5 text-muted-foreground">{s} · {t("pendiente")}</span>)}</div>
      )}
    </section>
  );
};
